/**
 * AJUSTES.
 *
 * Fuera de la barra inferior, como engranaje en la cabecera.
 *
 * Incluye el EXPORT JSON BÁSICO: la red de seguridad temprana del Ajuste 3. La
 * aplicación se empieza a usar de verdad tras la Fase 3, pero la sincronización
 * llega en la Fase 8; entre medio, el progreso no puede depender solo del
 * almacenamiento del navegador.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';

import {
  consultarUso,
  formatearBytes,
  solicitarPersistencia,
  type EstadoAlmacenamiento,
} from '../../app/almacenamiento';
import type { Tema } from '../../domain/modelo/ajustes';
import { ajustesRepo } from '../../persistencia/ajustesRepo';
import { contarRegistros, descargarExport } from '../../persistencia/exportar';
import {
  AVISO_REEMPLAZO,
  AVISO_SIN_AUDIO,
  analizarImport,
  restaurar,
  type Analisis,
} from '../../persistencia/importar';
import { Pantalla } from '../comunes/Pantalla';
import { Suave, Tarjeta } from '../comunes/Tarjeta';
import estilos from './Ajustes.module.css';

const TEMAS: { valor: Tema; etiqueta: string }[] = [
  { valor: 'sistema', etiqueta: 'El del sistema' },
  { valor: 'claro', etiqueta: 'Claro' },
  { valor: 'oscuro', etiqueta: 'Oscuro' },
];

const DURACIONES = [20, 35, 50] as const;

export function Ajustes() {
  const ajustes = useLiveQuery(() => ajustesRepo.obtener(), [], undefined);
  const registros = useLiveQuery(() => contarRegistros(), [], undefined);
  const [uso, setUso] = useState<string>('—');
  const [mensajeExport, setMensajeExport] = useState<string | null>(null);
  const [persistencia, setPersistencia] = useState<EstadoAlmacenamiento>('comprobando');
  const [analisis, setAnalisis] = useState<Analisis | null>(null);
  const [restaurando, setRestaurando] = useState(false);
  const [mensajeImport, setMensajeImport] = useState<string | null>(null);

  useEffect(() => {
    void consultarUso().then((r) => setUso(formatearBytes(r.usadoBytes)));
  }, [registros]);

  // Se pide en cada visita a Ajustes porque `persist()` es idempotente y
  // el navegador puede cambiar de opinión: instalar la PWA suele bastar
  // para que Chrome lo conceda, y esta pantalla es donde se comprueba.
  useEffect(() => {
    void solicitarPersistencia().then(setPersistencia);
  }, []);

  if (!ajustes) {
    return (
      <Pantalla titulo="Ajustes">
        <Suave>Cargando…</Suave>
      </Pantalla>
    );
  }

  async function exportar() {
    setMensajeExport(null);
    const nombre = await descargarExport();
    setMensajeExport(`Se descargó ${nombre}`);
  }

  /** Solo LEE el archivo y lo valida. No toca la base todavía. */
  async function revisarArchivo(archivo: File) {
    setMensajeImport(null);
    setAnalisis(analizarImport(await archivo.text()));
  }

  async function confirmarRestauracion() {
    if (!analisis?.ok) return;
    setRestaurando(true);
    try {
      const hecho = await restaurar(analisis.datos);
      setAnalisis(null);
      setMensajeImport(
        `Restaurado: ${hecho.sesiones} sesiones, ${hecho.banco} elementos del banco y ${hecho.grabaciones} fichas de grabación.`,
      );
    } finally {
      setRestaurando(false);
    }
  }

  return (
    <Pantalla titulo="Ajustes" tituloVisible>
      <Tarjeta titulo="Apariencia">
        <fieldset className={estilos.grupo}>
          <legend className={estilos.leyenda}>Tema</legend>
          {TEMAS.map((opcion) => (
            <label key={opcion.valor} className={estilos.opcion}>
              <input
                type="radio"
                name="tema"
                value={opcion.valor}
                checked={ajustes.tema === opcion.valor}
                onChange={() => void ajustesRepo.guardar({ tema: opcion.valor })}
              />
              {opcion.etiqueta}
            </label>
          ))}
        </fieldset>
        <Suave>
          El tema es de este dispositivo y no viaja: oscuro en el teléfono y claro en la PC
          es perfectamente válido.
        </Suave>
      </Tarjeta>

      <Tarjeta titulo="Duración preferida">
        <fieldset className={estilos.grupo}>
          <legend className={estilos.leyenda}>Se preselecciona al abrir una sesión</legend>
          {DURACIONES.map((duracion) => (
            <label key={duracion} className={estilos.opcion}>
              <input
                type="radio"
                name="duracion"
                value={duracion}
                checked={ajustes.duracionPreferida === duracion}
                onChange={() => void ajustesRepo.guardar({ duracionPreferida: duracion })}
              />
              {duracion} min
            </label>
          ))}
        </fieldset>
        <Suave>
          El día define qué entrenás. El tiempo disponible solo define cuánto profundizás.
        </Suave>
      </Tarjeta>

      <Tarjeta titulo="Respaldo">
        <Suave>
          Todavía no hay sincronización con la nube: llega en una fase posterior. Hasta
          entonces, descargar el respaldo es lo único que protege tu progreso si el
          navegador borra sus datos.
        </Suave>
        <p className={estilos.registros}>
          {registros ?? 0} registros · {uso} en este dispositivo
        </p>
        <button
          type="button"
          className={estilos.boton}
          onClick={() => void exportar()}
          disabled={!registros}
        >
          Descargar respaldo JSON
        </button>
        {!registros ? (
          <Suave>Todavía no hay nada que respaldar.</Suave>
        ) : null}
        {mensajeExport ? (
          <p className={estilos.exito} role="status">
            {mensajeExport}
          </p>
        ) : null}
      </Tarjeta>

      <Tarjeta titulo="Estado del almacenamiento">
        {persistencia === 'comprobando' ? (
          <Suave>Comprobando…</Suave>
        ) : persistencia === 'persistente' ? (
          <>
            <p className={estilos.estadoBueno}>Almacenamiento persistente concedido.</p>
            <Suave>
              El navegador se compromete a no descartar tus datos para hacer sitio. Aun
              así, borrar los datos del sitio a mano los borra igual: el respaldo sigue
              siendo tu red.
            </Suave>
          </>
        ) : persistencia === 'no_persistente' ? (
          <>
            <p className={estilos.estadoAviso}>Almacenamiento persistente NO concedido.</p>
            <Suave>
              El navegador puede descartar tus datos si le falta espacio. Instalar la
              aplicación desde el menú de Chrome suele bastar para que lo conceda.
              Mientras tanto, descargá el respaldo cada semana.
            </Suave>
          </>
        ) : (
          <>
            <p className={estilos.estadoAviso}>Este navegador no permite consultarlo.</p>
            <Suave>
              No podemos saber si tus datos están protegidos de un desalojo. No
              significa que estén en peligro; significa que no hay forma de
              comprobarlo desde acá.
            </Suave>
          </>
        )}
      </Tarjeta>

      <Tarjeta titulo="Restaurar un respaldo">
        <Suave>{AVISO_SIN_AUDIO}</Suave>

        <label className={estilos.cargar}>
          <span className={estilos.boton}>Elegir archivo JSON</span>
          <input
            type="file"
            accept="application/json,.json"
            className="sr-only"
            aria-label="Elegir un respaldo JSON para restaurar"
            onChange={(evento) => {
              const archivo = evento.target.files?.[0];
              if (archivo) void revisarArchivo(archivo);
              evento.target.value = '';
            }}
          />
        </label>

        {analisis && !analisis.ok ? (
          <p className={estilos.error} role="alert">
            {analisis.motivo}
          </p>
        ) : null}

        {analisis?.ok ? (
          <div className={estilos.resumenImport}>
            <p className={estilos.tituloResumen}>Esto es lo que se va a restaurar</p>
            <ul className={estilos.listaResumen}>
              <li>{analisis.resumen.sesiones} sesiones</li>
              <li>{analisis.resumen.banco} elementos del Learning Bank</li>
              <li>
                {analisis.resumen.grabaciones} fichas de grabación,
                {' '}sin audio ({analisis.resumen.grabacionesSinAudio} sin sonido)
              </li>
              <li>
                {analisis.resumen.traeAjustes ? 'Con tus ajustes' : 'Sin ajustes'}
              </li>
              {analisis.resumen.exportadoEn ? (
                <li>
                  Respaldo del{' '}
                  {new Date(analisis.resumen.exportadoEn).toLocaleString('es')}
                </li>
              ) : null}
            </ul>
            <p className={estilos.aviso}>{AVISO_REEMPLAZO}</p>
            <div className={estilos.accionesImport}>
              <button
                type="button"
                className={estilos.boton}
                onClick={() => void confirmarRestauracion()}
                disabled={restaurando}
              >
                {restaurando ? 'Restaurando…' : 'Restaurar y reemplazar'}
              </button>
              <button
                type="button"
                className={estilos.botonSecundario}
                onClick={() => setAnalisis(null)}
                disabled={restaurando}
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : null}

        {mensajeImport ? (
          <p className={estilos.exito} role="status">
            {mensajeImport}
          </p>
        ) : null}
      </Tarjeta>

      <Tarjeta titulo="Primeros pasos">
        <Suave>
          Podés volver a ver la explicación inicial del programa cuando quieras.
        </Suave>
        <button
          type="button"
          className={estilos.botonSecundario}
          onClick={() => void ajustesRepo.guardar({ onboardingVisto: false })}
        >
          Ver de nuevo la introducción
        </button>
      </Tarjeta>
    </Pantalla>
  );
}
